// HEAVY (server/loader-side) symbol resolution. These helpers read the full
// SymbolRecord index (symbol-index.generated.ts, ~1.3 MB) and must NOT be
// imported by client components — doing so drags the whole registry into the
// browser bundle. Client code that only needs a name or slug should use the
// lightweight maps in symbol-names.ts / symbols.ts instead.
import { getRecordBySlug, getRecordBySymbol, SYMBOLS } from "./symbol-index.generated";
import { getCategory, type SymbolEntry } from "./symbols";

// Single source of truth: the generated UCD-backed index. Curated `categories`
// only define grouping + presentation; per-symbol metadata (name, slug, block,
// script) is owned by symbol-index.generated.ts.
export const allSymbols: SymbolEntry[] = SYMBOLS.map((r) => ({
  symbol: r.symbol,
  slug: r.slug,
  categorySlug: r.categorySlug,
  categoryName: r.categoryName,
}));

export function findSymbolBySlug(slug: string): SymbolEntry | undefined {
  const r = getRecordBySlug(slug);
  if (!r) return undefined;
  return {
    symbol: r.symbol,
    slug: r.slug,
    categorySlug: r.categorySlug,
    categoryName: r.categoryName,
  };
}

// Returns previous & next siblings within the same category for crawl-depth nav.
export function getSiblingNav(entry: SymbolEntry) {
  const cat = getCategory(entry.categorySlug);
  if (!cat) return { prev: undefined, next: undefined, index: -1, total: 0 };
  const list = cat.symbols;
  const idx = list.indexOf(entry.symbol);
  if (idx === -1) return { prev: undefined, next: undefined, index: -1, total: list.length };
  const prevSym = idx > 0 ? list[idx - 1] : list[list.length - 1];
  const nextSym = idx < list.length - 1 ? list[idx + 1] : list[0];
  const toEntry = (s: string): SymbolEntry | undefined => {
    const r = getRecordBySymbol(s);
    return r
      ? { symbol: r.symbol, slug: r.slug, categorySlug: r.categorySlug, categoryName: r.categoryName }
      : undefined;
  };
  return {
    prev: toEntry(prevSym),
    next: toEntry(nextSym),
    index: idx,
    total: list.length,
  };
}

export interface RelatedSymbol {
  symbol: string;
  slug: string;
}

/** Resolve a list of slugs to {symbol,slug} pairs, dropping anything missing. */
export function resolveSlugs(slugs: string[]): RelatedSymbol[] {
  const out: RelatedSymbol[] = [];
  for (const slug of slugs) {
    const r = getRecordBySlug(slug);
    if (r) out.push({ symbol: r.symbol, slug: r.slug });
  }
  return out;
}

/** Visually confusable peers (UTS#39) — drives the "looks similar to" row. */
export function getLookalikes(symbol: string): RelatedSymbol[] {
  const r = getRecordBySymbol(symbol);
  if (!r) return [];
  return resolveSlugs(r.lookalikes);
}

/** Other indexed symbols sharing the same Unicode block (excluding self & same category). */
export function getBlockNeighbors(symbol: string, opts?: { excludeCategorySlug?: string; limit?: number }): RelatedSymbol[] {
  const r = getRecordBySymbol(symbol);
  if (!r) return [];
  const limit = opts?.limit ?? 12;
  const exclude = opts?.excludeCategorySlug;
  const resolved = resolveSlugs(r.blockNeighbors).filter((p) => {
    if (!exclude) return true;
    const peer = getRecordBySlug(p.slug);
    return peer?.categorySlug !== exclude;
  });
  return resolved.slice(0, limit);
}

/** Block name (e.g. "Arrows") of a given symbol, for headlines. */
export function getBlockName(symbol: string): string | null {
  return getRecordBySymbol(symbol)?.block ?? null;
}
